import { IInventory } from "../../services/inventoryApi";
import {
  AuctionBuyer,
  AuctionPackage,
  AuctionTotals,
  AuctionWin,
} from "../../services/liveAuctionsApi";
import {
  applyCurrentInventoryCost,
  InventoryMap,
  packageSellerCovered,
} from "../LiveAuctions/reporting";

export type ReportFilters = {
  startDate: string;
  endDate: string;
  buyerId?: string;
};

export type ReportWinRow = {
  winId: string;
  buyerId: string;
  username: string;
  dateKey: string;
  itemName: string;
  quantity: number;
  winningPriceCents: number;
  revenueCents: number;
  itemCostCents: number;
  platformFeeCents: number;
  packageId?: string;
  packageLabel?: string;
  packageStatus?: string;
  packageKnown: boolean;
  packagingAllocatedCents: number;
  shippingAllocatedCents: number;
  shippingRecoveryAllocatedCents: number;
  sellerCoveredShippingAllocatedCents: number;
  netProfitCents: number;
  marginPercent: number;
  winStatus: string;
};

export type ReportSummary = {
  totalRevenueCents: number;
  totalWins: number;
  paidWins: number;
  totalBuyers: number;
  averageWinningPriceCents: number;
  averageRevenuePerBuyerCents: number;
  itemCostCents: number;
  platformFeesCents: number;
  shippingCostCents: number;
  packagingCostCents: number;
  shippingRecoveryCents: number;
  totalExpensesCents: number;
  grossProfitCents: number;
  netProfitCents: number;
  marginPercent: number;
  profitPerWinCents: number;
  profitPerBuyerCents: number;
  unreconciledWinUnits: number;
  unreconciledWinLines: number;
  reconciledPackageCount: number;
};

export type BuyerReportRow = {
  buyerId: string;
  username: string;
  wins: number;
  paidWins: number;
  revenueCents: number;
  itemCostCents: number;
  platformFeesCents: number;
  shippingCostCents: number;
  packagingCostCents: number;
  shippingRecoveryCents: number;
  totalCostCents: number;
  netProfitCents: number;
  marginPercent: number;
  packageCount: number;
  unreconciledWinUnits: number;
  reconciliationStatus: string;
};

export type DailyReportRow = {
  dateKey: string;
  salesCents: number;
  wins: number;
  buyers: number;
  expensesCents: number;
  profitCents: number;
  marginPercent: number;
};

export type ExpenseBreakdownRow = {
  name: string;
  valueCents: number;
};

export type AuctionReportData = {
  summary: ReportSummary;
  breakdown: ExpenseBreakdownRow[];
  buyerRows: BuyerReportRow[];
  dailyRows: DailyReportRow[];
  detailRows: ReportWinRow[];
  topBuyerRevenueRows: Array<{ username: string; revenueCents: number }>;
  topBuyerProfitRows: Array<{ username: string; netProfitCents: number }>;
  filteredBuyer?: AuctionBuyer | null;
};

const toDateKey = (value: string | Date) => {
  const date = typeof value === "string" ? new Date(value) : value;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const roundToInt = (value: number) => Math.round(Number(value || 0));

const allocateByUnits = (
  totalCents: number,
  wins: AuctionWin[],
): Map<string, number> => {
  const result = new Map<string, number>();
  const entries = wins.map((win) => ({
    win,
    units: Math.max(0, Number(win.quantity || 0)),
  }));
  const totalUnits = entries.reduce((sum, row) => sum + row.units, 0);
  if (!totalCents || !totalUnits) {
    entries.forEach(({ win }) => result.set(win._id, 0));
    return result;
  }

  const provisional = entries.map(({ win, units }) => {
    const exact = (totalCents * units) / totalUnits;
    const floor = Math.floor(exact);
    return {
      winId: win._id,
      floor,
      remainder: exact - floor,
    };
  });

  let assigned = provisional.reduce((sum, row) => sum + row.floor, 0);
  const remaining = totalCents - assigned;
  provisional
    .sort((a, b) => b.remainder - a.remainder)
    .forEach((row, index) => {
      const extra = index < remaining ? 1 : 0;
      result.set(row.winId, row.floor + extra);
      assigned += extra;
    });

  return result;
};

const sumCents = (rows: ReportWinRow[], pick: (row: ReportWinRow) => number) =>
  rows.reduce((sum, row) => sum + roundToInt(pick(row)), 0);

const buildPackageLookup = (
  wins: AuctionWin[],
  packages: AuctionPackage[],
) => {
  const winsById = new Map(wins.map((win) => [win._id, win]));
  const packageIndex = new Map<string, number>();
  packages.forEach((pkg, index) => packageIndex.set(pkg._id, index + 1));
  const packageByWinId = new Map<
    string,
    {
      packageId: string;
      packageLabel: string;
      packageStatus: string;
      packagingAllocatedCents: number;
      shippingAllocatedCents: number;
      shippingRecoveryAllocatedCents: number;
      sellerCoveredShippingAllocatedCents: number;
    }
  >();

  packages
    .filter((pkg) => pkg.status !== "CANCELLED")
    .forEach((pkg) => {
      const packageWins = pkg.auctionWinIds
        .map((id) => winsById.get(String(id)))
        .filter(Boolean) as AuctionWin[];
      const packagingByWin = allocateByUnits(pkg.packagingCostCents, packageWins);
      const shippingByWin = allocateByUnits(
        pkg.actualShippingCostCents,
        packageWins,
      );
      const shippingRecoveryByWin = allocateByUnits(
        pkg.customerPaidShippingCents + pkg.platformShippingCreditCents,
        packageWins,
      );
      const sellerCoveredByWin = allocateByUnits(
        packageSellerCovered(pkg),
        packageWins,
      );

      packageWins.forEach((win) => {
        packageByWinId.set(win._id, {
          packageId: pkg._id,
          packageLabel:
            pkg.boxSizeLabel?.trim() ||
            `Box ${packageIndex.get(pkg._id) || ""}`.trim(),
          packageStatus: pkg.status,
          packagingAllocatedCents: packagingByWin.get(win._id) || 0,
          shippingAllocatedCents: shippingByWin.get(win._id) || 0,
          shippingRecoveryAllocatedCents:
            shippingRecoveryByWin.get(win._id) || 0,
          sellerCoveredShippingAllocatedCents:
            sellerCoveredByWin.get(win._id) || 0,
        });
      });
    });

  return packageByWinId;
};

const withinRange = (win: AuctionWin, startDate: string, endDate: string) => {
  const key = toDateKey(win.saleDate);
  return key >= startDate && key <= endDate;
};

export const buildAuctionReport = ({
  wins,
  packages,
  inventory,
  buyers,
  filters,
}: {
  wins: AuctionWin[];
  packages: AuctionPackage[];
  inventory: IInventory[];
  buyers: AuctionBuyer[];
  filters: ReportFilters;
}): AuctionReportData => {
  const inventoryMap: InventoryMap = new Map(
    inventory.map((item) => [item._id, item]),
  );
  const syncedWins = wins.map((win) => applyCurrentInventoryCost(win, inventoryMap));
  const packageLookup = buildPackageLookup(syncedWins, packages);

  const filteredWins = syncedWins.filter((win) => {
    if (!withinRange(win, filters.startDate, filters.endDate)) return false;
    if (filters.buyerId && String(win.auctionBuyerId) !== String(filters.buyerId))
      return false;
    return true;
  });

  const detailRows = filteredWins.map((win) => {
    const revenueCents =
      win.status === "PAID" ? win.winningPriceCents * win.quantity : 0;
    const itemCostCents =
      win.status === "PAID" ? win.costPerUnitCents * win.quantity : 0;
    const platformFeeCents = win.status === "PAID" ? win.platformFeeCents : 0;
    const allocation = packageLookup.get(win._id);
    const packagingAllocatedCents = allocation?.packagingAllocatedCents || 0;
    const shippingAllocatedCents = allocation?.shippingAllocatedCents || 0;
    const shippingRecoveryAllocatedCents =
      allocation?.shippingRecoveryAllocatedCents || 0;
    const netProfitCents =
      revenueCents +
      shippingRecoveryAllocatedCents -
      itemCostCents -
      platformFeeCents -
      packagingAllocatedCents -
      shippingAllocatedCents;
    return {
      winId: win._id,
      buyerId: win.auctionBuyerId,
      username: win.usernameSnapshot,
      dateKey: toDateKey(win.saleDate),
      itemName: win.inventoryItemNameSnapshot,
      quantity: win.quantity,
      winningPriceCents: win.winningPriceCents,
      revenueCents,
      itemCostCents,
      platformFeeCents,
      packageId: allocation?.packageId,
      packageLabel: allocation?.packageLabel,
      packageStatus: allocation?.packageStatus,
      packageKnown: Boolean(allocation),
      packagingAllocatedCents,
      shippingAllocatedCents,
      shippingRecoveryAllocatedCents,
      sellerCoveredShippingAllocatedCents:
        allocation?.sellerCoveredShippingAllocatedCents || 0,
      netProfitCents,
      marginPercent: revenueCents
        ? Math.round((netProfitCents / revenueCents) * 10000) / 100
        : 0,
      winStatus: win.status,
    } satisfies ReportWinRow;
  });

  const totalRevenueCents = sumCents(detailRows, (row) => row.revenueCents);
  const itemCostCents = sumCents(detailRows, (row) => row.itemCostCents);
  const platformFeesCents = sumCents(detailRows, (row) => row.platformFeeCents);
  const packagingCostCents = sumCents(
    detailRows,
    (row) => row.packagingAllocatedCents,
  );
  const shippingCostCents = sumCents(
    detailRows,
    (row) => row.shippingAllocatedCents,
  );
  const shippingRecoveryCents = sumCents(
    detailRows,
    (row) => row.shippingRecoveryAllocatedCents,
  );
  const totalWins = filteredWins.reduce((sum, win) => sum + win.quantity, 0);
  const paidWins = filteredWins
    .filter((win) => win.status === "PAID")
    .reduce((sum, win) => sum + win.quantity, 0);
  const buyersInScope = Array.from(
    new Set(filteredWins.map((win) => String(win.auctionBuyerId))),
  );
  const unreconciledWins = detailRows.filter((row) => !row.packageKnown);
  const totalExpensesCents =
    itemCostCents + platformFeesCents + packagingCostCents + shippingCostCents;
  const grossProfitCents = totalRevenueCents - itemCostCents;
  const netProfitCents =
    totalRevenueCents +
    shippingRecoveryCents -
    itemCostCents -
    platformFeesCents -
    packagingCostCents -
    shippingCostCents;
  const summary: ReportSummary = {
    totalRevenueCents,
    totalWins,
    paidWins,
    totalBuyers: buyersInScope.length,
    averageWinningPriceCents: paidWins
      ? Math.round(totalRevenueCents / paidWins)
      : 0,
    averageRevenuePerBuyerCents: buyersInScope.length
      ? Math.round(totalRevenueCents / buyersInScope.length)
      : 0,
    itemCostCents,
    platformFeesCents,
    shippingCostCents,
    packagingCostCents,
    shippingRecoveryCents,
    totalExpensesCents,
    grossProfitCents,
    netProfitCents,
    marginPercent: totalRevenueCents
      ? Math.round((netProfitCents / totalRevenueCents) * 10000) / 100
      : 0,
    profitPerWinCents: totalWins ? Math.round(netProfitCents / totalWins) : 0,
    profitPerBuyerCents: buyersInScope.length
      ? Math.round(netProfitCents / buyersInScope.length)
      : 0,
    unreconciledWinUnits: unreconciledWins.reduce(
      (sum, row) => sum + row.quantity,
      0,
    ),
    unreconciledWinLines: unreconciledWins.length,
    reconciledPackageCount: new Set(
      detailRows.filter((row) => row.packageId).map((row) => row.packageId),
    ).size,
  };

  const buyerRowMap = new Map<string, BuyerReportRow>();
  detailRows.forEach((row) => {
    const current = buyerRowMap.get(row.buyerId) || {
      buyerId: row.buyerId,
      username: row.username,
      wins: 0,
      paidWins: 0,
      revenueCents: 0,
      itemCostCents: 0,
      platformFeesCents: 0,
      shippingCostCents: 0,
      packagingCostCents: 0,
      shippingRecoveryCents: 0,
      totalCostCents: 0,
      netProfitCents: 0,
      marginPercent: 0,
      packageCount: 0,
      unreconciledWinUnits: 0,
      reconciliationStatus: "Awaiting reconciliation",
    };
    current.wins += row.quantity;
    if (row.revenueCents > 0) current.paidWins += row.quantity;
    current.revenueCents += row.revenueCents;
    current.itemCostCents += row.itemCostCents;
    current.platformFeesCents += row.platformFeeCents;
    current.shippingCostCents += row.shippingAllocatedCents;
    current.packagingCostCents += row.packagingAllocatedCents;
    current.shippingRecoveryCents += row.shippingRecoveryAllocatedCents;
    current.netProfitCents += row.netProfitCents;
    current.unreconciledWinUnits += row.packageKnown ? 0 : row.quantity;
    buyerRowMap.set(row.buyerId, current);
  });

  const buyerPackageCounts = new Map<string, Set<string>>();
  detailRows.forEach((row) => {
    if (!row.packageId) return;
    const set = buyerPackageCounts.get(row.buyerId) || new Set<string>();
    set.add(row.packageId);
    buyerPackageCounts.set(row.buyerId, set);
  });

  const buyerRows = Array.from(buyerRowMap.values())
    .map((row) => {
      row.packageCount = buyerPackageCounts.get(row.buyerId)?.size || 0;
      row.totalCostCents =
        row.itemCostCents +
        row.platformFeesCents +
        row.shippingCostCents +
        row.packagingCostCents;
      row.marginPercent = row.revenueCents
        ? Math.round((row.netProfitCents / row.revenueCents) * 10000) / 100
        : 0;
      row.reconciliationStatus =
        row.unreconciledWinUnits === 0
          ? "Reconciled"
          : row.packageCount
            ? "Partially reconciled"
            : "Awaiting reconciliation";
      return row;
    })
    .sort((a, b) => b.revenueCents - a.revenueCents);

  const dailyMap = new Map<string, DailyReportRow>();
  detailRows.forEach((row) => {
    const current = dailyMap.get(row.dateKey) || {
      dateKey: row.dateKey,
      salesCents: 0,
      wins: 0,
      buyers: 0,
      expensesCents: 0,
      profitCents: 0,
      marginPercent: 0,
    };
    current.salesCents += row.revenueCents;
    current.wins += row.quantity;
    current.expensesCents +=
      row.itemCostCents +
      row.platformFeeCents +
      row.packagingAllocatedCents +
      row.shippingAllocatedCents;
    current.profitCents += row.netProfitCents;
    dailyMap.set(row.dateKey, current);
  });
  Array.from(dailyMap.keys()).forEach((dateKey) => {
    const buyersOnDay = new Set(
      detailRows
        .filter((row) => row.dateKey === dateKey)
        .map((row) => `${row.buyerId}:${row.username}`),
    );
    const row = dailyMap.get(dateKey)!;
    row.buyers = buyersOnDay.size;
    row.marginPercent = row.salesCents
      ? Math.round((row.profitCents / row.salesCents) * 10000) / 100
      : 0;
  });
  const dailyRows = Array.from(dailyMap.values()).sort((a, b) =>
    a.dateKey.localeCompare(b.dateKey),
  );

  const breakdown: ExpenseBreakdownRow[] = [
    { name: "Inventory", valueCents: itemCostCents },
    { name: "Platform fees", valueCents: platformFeesCents },
    { name: "Shipping", valueCents: shippingCostCents },
    { name: "Packaging", valueCents: packagingCostCents },
  ];

  const topBuyerRevenueRows = buyerRows
    .slice()
    .sort((a, b) => b.revenueCents - a.revenueCents)
    .slice(0, 8)
    .map((row) => ({
      username: row.username,
      revenueCents: row.revenueCents,
    }));

  const topBuyerProfitRows = buyerRows
    .slice()
    .sort((a, b) => b.netProfitCents - a.netProfitCents)
    .slice(0, 8)
    .map((row) => ({
      username: row.username,
      netProfitCents: row.netProfitCents,
    }));

  return {
    summary,
    breakdown,
    buyerRows,
    dailyRows,
    detailRows,
    topBuyerRevenueRows,
    topBuyerProfitRows,
    filteredBuyer:
      buyers.find((buyer) => String(buyer._id) === String(filters.buyerId)) ||
      null,
  };
};

export const sortBuyerRows = (
  rows: BuyerReportRow[],
  key: "revenue" | "profit" | "margin" | "wins" | "shipping",
  direction: "asc" | "desc",
) => {
  const multiplier = direction === "asc" ? 1 : -1;
  const valueFor = (row: BuyerReportRow) => {
    switch (key) {
      case "profit":
        return row.netProfitCents;
      case "margin":
        return row.marginPercent;
      case "wins":
        return row.wins;
      case "shipping":
        return row.shippingCostCents;
      case "revenue":
      default:
        return row.revenueCents;
    }
  };
  return rows.slice().sort((a, b) => {
    const diff = valueFor(a) - valueFor(b);
    if (diff !== 0) return diff * multiplier;
    return a.username.localeCompare(b.username) * multiplier;
  });
};

export const csvEscape = (value: string | number) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

